import * as React from 'react'
import { chargebeeInstance, loadChargebeeJs } from './useChargebeeCheckout'

// PayPal, Apple Pay and Google Pay through Chargebee.js. Each intent is bound
// to one method, so every button gets its own at the settled price, which means
// a page view creates a Chargebee customer (the card-only path avoids that).
// Any failure hides that button only; the card is untouched.
const METHODS = {
    paypal: {
        module: 'paypal',
        paymentMethodType: 'paypal_express_checkout',
        // 45px like the Google/Apple buttons; no tagline row underneath.
        buttonOptions: {
            style: {
                layout: 'horizontal',
                color: 'gold',
                shape: 'rect',
                height: 45,
                tagline: false,
            },
        },
    },
    google_pay: {
        module: 'google-pay',
        paymentMethodType: 'google_pay',
        afterMount: enableGooglePayPopupRecovery,
    },
    apple_pay: {
        module: 'apple-pay',
        paymentMethodType: 'apple_pay',
        // Outside Safari there is no Apple Pay: skip it rather than wait on it.
        supported: () => {
            try {
                return Boolean(
                    window.ApplePaySession &&
                        window.ApplePaySession.canMakePayments()
                )
            } catch (error) {
                return false
            }
        },
    },
}

// Outside Chrome the Google Pay sheet is a popup, and Safari refuses a second
// one on the same page ("failed to open window"). With this pay.js flag Google
// shows its own prompt instead, whose tap reopens the sheet. pay.js is on the
// page once Chargebee has mounted the button.
function enableGooglePayPopupRecovery() {
    const googlePayApi = window.google?.payments?.api
    if (googlePayApi) googlePayApi.EnablePopupFailureHandling = true
}

export const WALLET_LABELS = {
    paypal: 'PayPal',
    google_pay: 'Google Pay',
    apple_pay: 'Apple Pay',
}

// An optional button must never hold the card hostage: a method whose
// Chargebee.js module or intent has not answered by then counts as unavailable.
const METHOD_SETUP_LIMIT_MS = 8000

function withinLimit(promise) {
    let timer
    const limit = new Promise((resolve, reject) => {
        timer = setTimeout(
            () => reject(new Error('setup timed out')),
            METHOD_SETUP_LIMIT_MS
        )
    })
    return Promise.race([promise, limit]).finally(() => clearTimeout(timer))
}

// Chargebee.js hands PayPal's success callback the intent itself, but Google
// Pay's and Apple Pay's a `{ paymentIntent }` wrapper.
function authorizedIntent(result) {
    return result && result.paymentIntent ? result.paymentIntent : result
}

export function walletContainerId(method) {
    return `cb-wallet-${method.replace(/_/g, '-')}`
}

export function useWalletPayment({
    enabled,
    site,
    publishableKey,
    amountKey,
    requestIntent,
    onStarted,
    onCancelled,
    onAuthorized,
    onError,
}) {
    const [ready, setReady] = React.useState({})
    const [unavailable, setUnavailable] = React.useState({})
    // The price the buttons were last fully set up for; `settled` compares it
    // with the current one, so a price change reads as unsettled at once.
    const [settledFor, setSettledFor] = React.useState(null)
    // Read through a ref so a new callback identity never re-runs the setup —
    // re-running would create another round of payment intents.
    const callbacksRef = React.useRef({})
    callbacksRef.current = {
        requestIntent,
        onStarted,
        onCancelled,
        onAuthorized,
        onError,
    }
    // Chargebee.js hands back the same handler for a method on every round, so
    // two rounds must never set up at once: an earlier round still mounting
    // PayPal into a slot that has gone reports "element … does not exist"
    // through the current round's error callback, as a failed payment.
    const previousSetupRef = React.useRef(Promise.resolve())
    const currentRoundRef = React.useRef(null)

    React.useEffect(() => {
        if (!enabled || !site || !publishableKey || !amountKey) return undefined
        let cancelled = false
        // Each price gets new intents; each round mounts into fresh slots,
        // removed on change, so a leftover button can never authorise a stale
        // amount.
        const round = Math.random()
            .toString(36)
            .slice(2)
        currentRoundRef.current = round
        const slots = []
        const slotFor = method => {
            const container = document.getElementById(walletContainerId(method))
            if (!container) return null
            const slot = document.createElement('div')
            slot.id = `${walletContainerId(method)}-${round}`
            container.appendChild(slot)
            slots.push(slot)
            return slot
        }
        setReady({})
        setUnavailable({})
        const markUnavailable = method =>
            setUnavailable(current => ({ ...current, [method]: true }))
        const reportError = (error, method) => {
            if (currentRoundRef.current !== round) {
                console.warn(
                    `[wallet] ${method} error from a replaced button:`,
                    error && error.message
                )
                return
            }
            callbacksRef.current.onError(error, method)
        }

        const setUpRound = async () => {
            // Replaced while it waited for the previous round: nothing to do.
            if (cancelled) return
            try {
                const Chargebee = await loadChargebeeJs()
                const instance = chargebeeInstance(Chargebee, {
                    site,
                    publishableKey,
                })
                const candidates = Object.entries(METHODS).filter(
                    ([method, { supported }]) => {
                        if (!supported || supported()) return true
                        markUnavailable(method)
                        return false
                    }
                )
                // The modules load side by side; only the intents go one at a
                // time, because the first may create the Chargebee customer
                // and parallel creates would collide.
                const handlers = Object.fromEntries(
                    candidates.map(([method, { module }]) => {
                        const loading = withinLimit(instance.load(module))
                        loading.catch(() => {})
                        return [method, loading]
                    })
                )
                for (const [
                    method,
                    { paymentMethodType, buttonOptions, afterMount },
                ] of candidates) {
                    try {
                        const handler = await handlers[method]
                        const checkout = await withinLimit(
                            callbacksRef.current.requestIntent(
                                paymentMethodType
                            )
                        )
                        if (cancelled) return
                        const slot =
                            checkout && checkout.paymentIntent
                                ? slotFor(method)
                                : null
                        if (!slot) {
                            markUnavailable(method)
                            continue
                        }
                        handler.setPaymentIntent(checkout.paymentIntent)
                        await withinLimit(
                            handler.mountPaymentButton(
                                `#${slot.id}`,
                                buttonOptions
                            )
                        )
                        if (cancelled) return
                        if (afterMount) afterMount()
                        setReady(current => ({ ...current, [method]: true }))
                        // The button is Chargebee's own; these callbacks fire on
                        // every attempt, so a closed popup can be retried.
                        handler.handlePayment({
                            click: () => callbacksRef.current.onStarted(method),
                            cancel: () =>
                                callbacksRef.current.onCancelled(method),
                            success: result =>
                                callbacksRef.current.onAuthorized(
                                    authorizedIntent(result),
                                    method
                                ),
                            // PayPal and Google Pay pass (intent, error);
                            // Apple Pay forwards only the intent.
                            error: (intentOrError, error) =>
                                reportError(error || intentOrError, method),
                        })
                    } catch (error) {
                        if (cancelled) return
                        markUnavailable(method)
                        console.warn(
                            `[wallet] ${method} unavailable:`,
                            error && error.message
                        )
                    }
                }
                if (!cancelled) setSettledFor(amountKey)
            } catch (error) {
                if (cancelled) return
                for (const method of Object.keys(METHODS))
                    markUnavailable(method)
                setSettledFor(amountKey)
                console.warn('[wallet] setup failed:', error && error.message)
            }
        }
        const setup = previousSetupRef.current.then(setUpRound)
        previousSetupRef.current = setup

        return () => {
            cancelled = true
            // Hidden at once so a stale button can't be pressed; removed only
            // once its round has stopped working with it.
            slots.forEach(slot => {
                slot.hidden = true
            })
            setup.finally(() => slots.forEach(slot => slot.remove()))
        }
    }, [enabled, site, publishableKey, amountKey])

    const active = Boolean(enabled && site && publishableKey && amountKey)
    return {
        ready,
        methods: Object.keys(METHODS).filter(method => !unavailable[method]),
        settled: !active || settledFor === amountKey,
    }
}
